import { useState } from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faArrowLeftLong,
  faBook,
  faBrain,
  faBug,
  faCog,
  faFileMagnifyingGlass,
  faKey,
  faPlug,
  faUserCircle,
} from "@fortawesome/pro-regular-svg-icons";
import { Link, NavLink } from "react-router-dom";
import { twJoin } from "tailwind-merge";

import { ApiKeyOverview } from "src/apiKeys/ApiKeysOverview";
import { AuditLogsPage } from "src/auditLogs/AuditLogsPage";
import { Icon } from "src/base-components/Icon";
import { SimpleRoleType } from "src/clients/taktile-api";
import { ConnectionOverview } from "src/connections/ConnectionOverview";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useDocumentTitle } from "src/hooks/useDocumentTitle";
import { useIsSidebarLayout } from "src/hooks/useIsSidebarLayout";
import { DashboardContent } from "src/layout/DashboardContent";
import { OrgLogoModal } from "src/layout/OrgLogoModal/OrgLogoModal";
import { PageHeader } from "src/layout/PageHeader";
import { WorkspaceFormModal } from "src/layout/WorkspaceFormModal/WorkspaceFormModal";
import { MainSideBar } from "src/mainPage/MainSideBar";
import { DebugSidePane } from "src/powertools/DebugPane";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import {
  DashboardPageParams,
  getUrlToSettingsPage,
  getUrlToWsDashboard,
} from "src/router/urls";
import { AgentCredentialsOverview } from "src/settings/AgentCredentialsOverview";
import { UsersPermissionsPage } from "src/userManagement/UsersPermissionsPage";
import { useWorkspaceAndOrg } from "src/utils/useCurrentWorkspace";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const HeaderBackLink: React.FC<{ to: string; dataLoc?: string }> = ({
  to,
  dataLoc,
}) => (
  <NavLink data-loc={dataLoc} to={to}>
    <Icon
      color="text-gray-500 hover:text-gray-700"
      dataLoc="back-icon"
      icon={faArrowLeftLong}
      size="xs"
    />
  </NavLink>
);

const SettingsHeader: React.FC = () => {
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  return (
    <div className="w-full border-gray-200 border-b bg-white shadow-sm">
      <div className="flex h-12 items-center pl-5 font-inter-semibold-14px text-gray-800">
        <div className="mr-3 ml-0.5">
          <HeaderBackLink
            dataLoc="back-to-versions"
            to={getUrlToWsDashboard({ orgId, wsId })}
          />
        </div>
        Settings
      </div>
    </div>
  );
};

const NavItem: React.FC<{
  selected: boolean;
  page: SettingsSubpages;
  icon: IconProp;
  title: string;
}> = ({ selected, page, icon, title }) => {
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  return (
    <Link
      className={twJoin(linkClassName, selected && "bg-gray-50")}
      to={getUrlToSettingsPage(orgId, wsId, page, {
        keepFlags: true,
      })}
    >
      <Icon color="text-gray-500" icon={icon} size="xs" />
      <p className="font-inter-medium-13px text-gray-800">{title}</p>
    </Link>
  );
};

export enum SettingsSubpages {
  Connections = "connections",
  ApiKeys = "api-keys",
  AgentCredentials = "agent-credentials",
  Users = "users",
  AuditLogs = "audit-logs",
  Powertools = "powertools",
}

type SettingsNavBarProps = {
  currentSubpage: SettingsSubpages;
  setIsWsModalOpen: (open: boolean) => void;
  setIsOrgModalOpen: (open: boolean) => void;
};

const linkClassName =
  "flex flex-row items-center gap-x-2 rounded-lg px-2 py-1.5 hover:bg-gray-100";

const SettingsNavBar: React.FC<SettingsNavBarProps> = ({
  currentSubpage,
  setIsWsModalOpen,
  setIsOrgModalOpen,
}) => {
  const { org, workspace } = useWorkspaceAndOrg();
  const capabilities = useCapabilities();

  const isPowerToolsEnabled = isFeatureFlagEnabled(FEATURE_FLAGS.powertools);
  const { role } = useCapabilities();

  return (
    <div className="flex h-full w-52 shrink-0 flex-col gap-y-2 bg-white px-2.5 py-4 shadow-base">
      {capabilities.connections.canAccess && (
        <NavItem
          icon={faPlug}
          page={SettingsSubpages.Connections}
          selected={SettingsSubpages.Connections === currentSubpage}
          title="Connections"
        />
      )}
      {capabilities.apiKeys.canAccess &&
        isFeatureFlagEnabled(FEATURE_FLAGS.byokLlmCredentials) && (
          <NavItem
            icon={faBrain}
            page={SettingsSubpages.AgentCredentials}
            selected={SettingsSubpages.AgentCredentials === currentSubpage}
            title="AI credentials"
          />
        )}
      {capabilities.apiKeys.canAccess && (
        <NavItem
          icon={faKey}
          page={SettingsSubpages.ApiKeys}
          selected={SettingsSubpages.ApiKeys === currentSubpage}
          title="API keys"
        />
      )}
      {org?.whitelabel && capabilities.organization.canEditLogo && (
        <button
          className={linkClassName}
          type="button"
          onClick={() => setIsOrgModalOpen(true)}
        >
          <Icon color="text-gray-500" icon={faCog} size="xs" />
          <span className="font-inter-medium-13px text-gray-800">
            Organization settings
          </span>
        </button>
      )}
      {capabilities.usersPermissions.canAccess && (
        <NavItem
          icon={faUserCircle}
          page={SettingsSubpages.Users}
          selected={SettingsSubpages.Users === currentSubpage}
          title="User permissions"
        />
      )}
      {capabilities.auditLogs.canAccess && (
        <NavItem
          icon={faFileMagnifyingGlass}
          page={SettingsSubpages.AuditLogs}
          selected={SettingsSubpages.AuditLogs === currentSubpage}
          title="Audit log"
        />
      )}
      {capabilities.workspaces.canEdit && (
        <button
          className={linkClassName}
          type="button"
          onClick={() => setIsWsModalOpen(true)}
        >
          <Icon color="text-gray-500" icon={faCog} size="xs" />
          <span className="font-inter-medium-13px text-gray-800">
            Workspace settings
          </span>
        </button>
      )}
      {capabilities.apiDocs.canAccess && workspace?.base_url && (
        <button
          className={linkClassName}
          type="button"
          onClick={() => {
            window.open(`https://${workspace.base_url}/public/docs`, "_blank");
          }}
        >
          <Icon color="text-gray-500" icon={faBook} size="xs" />
          <span className="font-inter-medium-13px text-gray-800">
            API documentation
          </span>
        </button>
      )}
      {role === SimpleRoleType.ADMIN && isPowerToolsEnabled && (
        <NavItem
          icon={faBug}
          page={SettingsSubpages.Powertools}
          selected={SettingsSubpages.Powertools === currentSubpage}
          title="Debug"
        />
      )}
    </div>
  );
};

export const SettingsPage: React.FC<{ subpage: SettingsSubpages }> = ({
  subpage: currrentSubpage,
}) => {
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const [isWsModalOpen, setIsWsModalOpen] = useState(false);
  const [isOrgModalOpen, setIsOrgModalOpen] = useState(false);
  const isSideBarEnabled = useIsSidebarLayout();

  useDocumentTitle(["Settings"]);

  const renderPage = () => {
    switch (currrentSubpage) {
      case SettingsSubpages.Connections:
        return <ConnectionOverview workspaceId={wsId} />;
      case SettingsSubpages.ApiKeys:
        return <ApiKeyOverview organizationId={orgId} workspaceId={wsId} />;
      case SettingsSubpages.AgentCredentials:
        return <AgentCredentialsOverview />;
      case SettingsSubpages.Users:
        return <UsersPermissionsPage />;
      case SettingsSubpages.AuditLogs:
        return <AuditLogsPage />;
      case SettingsSubpages.Powertools:
        return <DebugSidePane />;
    }
  };

  return isSideBarEnabled ? (
    <DashboardContent sidebar={<MainSideBar />} removePadding>
      <PageHeader title="Settings" />
      <div className="flex h-full min-h-0 flex-grow flex-row border-gray-100 border-l">
        <SettingsNavBar
          currentSubpage={currrentSubpage}
          setIsOrgModalOpen={setIsOrgModalOpen}
          setIsWsModalOpen={setIsWsModalOpen}
        />
        <div className="decideScrollbar flex h-full flex-grow flex-col overflow-auto">
          {renderPage()}
        </div>
      </div>
      <WorkspaceFormModal
        isOpen={isWsModalOpen}
        orgId={orgId}
        variant="Edit"
        wsId={wsId}
        onClose={() => setIsWsModalOpen(false)}
      />
      <OrgLogoModal
        isOpen={isOrgModalOpen}
        onClose={() => setIsOrgModalOpen(false)}
      />
    </DashboardContent>
  ) : (
    <div className="flex h-screen w-screen flex-col">
      <SettingsHeader />
      <div className="flex min-h-0 flex-grow flex-row">
        <SettingsNavBar
          currentSubpage={currrentSubpage}
          setIsOrgModalOpen={setIsOrgModalOpen}
          setIsWsModalOpen={setIsWsModalOpen}
        />
        <div className="decideScrollbar flex h-full flex-grow flex-col overflow-auto">
          {renderPage()}
        </div>
      </div>
      <WorkspaceFormModal
        isOpen={isWsModalOpen}
        orgId={orgId}
        variant="Edit"
        wsId={wsId}
        onClose={() => setIsWsModalOpen(false)}
      />
      <OrgLogoModal
        isOpen={isOrgModalOpen}
        onClose={() => setIsOrgModalOpen(false)}
      />
    </div>
  );
};
